import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  ChevronLeft,
  Loader2,
  Save,
  AlertCircle,
  Upload,
  X,
  Image
} from 'lucide-react';
import { toast } from 'sonner';

const AdminOpportunityFormPage = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!opportunityId;
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    slug: '',
    project_code: '',
    status: 'draft',
    project_type: '',
    investment_structure: '',
    summary: '',
    summary_en: '',
    thesis: '',
    location_address: '',
    location_city: '',
    location_state: '',
    location_country: 'México',
    sponsor_name: '',
    sponsor_info: '',
    capital_target: '',
    capital_target_currency: 'MXN',
    minimum_investment: '',
    maximum_investment: '',
    timeline_months: '',
    hold_period_months: '',
    projected_irr_base: '',
    projected_irr_upside: '',
    projected_irr_downside: '',
    projected_moic_base: '',
    risk_level: 'medium',
    legal_structure: '',
    fees_management: '',
    fees_performance: '',
    fees_acquisition: '',
    use_of_funds: '',
    is_featured: false,
    requires_accreditation: false,
    gallery_images: [],
    developer_name: '',
    operator_name: '',
    // Estructura de Capital
    capital_structure: '',
    capital_equity: '',
    capital_debt: '',
    capital_mezzanine: '',
    // Uso de Fondos detallado
    use_of_funds_land: '',
    use_of_funds_construction: '',
    use_of_funds_softcosts: '',
    use_of_funds_reserves: '',
    use_of_funds_other: '',
    // Comisiones y Costos
    fees_structuring: '',
    fees_legal: '',
    fees_other: '',
    // Supuestos Clave
    assumptions_occupancy: '',
    assumptions_construction_cost: '',
    assumptions_sales_projection: '',
    assumptions_other: '',
  });

  useEffect(() => {
    if (isEditing) {
      fetchOpportunity();
    }
  }, [opportunityId]);

  const fetchOpportunity = async () => {
    try {
      // Get by opportunity_id from admin endpoint
      const response = await api.get('/admin/opportunities', { params: { limit: 100 } });
      const opp = response.data.opportunities?.find(o => o.opportunity_id === opportunityId);
      
      if (opp) {
        setFormData({
          title: opp.title || '',
          title_en: opp.title_en || '',
          slug: opp.slug || '',
          project_code: opp.project_code || '',
          status: opp.status || 'draft',
          project_type: opp.project_type || '',
          investment_structure: opp.investment_structure || '',
          summary: opp.summary || '',
          summary_en: opp.summary_en || '',
          thesis: opp.thesis || '',
          location_address: opp.location_address || '',
          location_city: opp.location_city || '',
          location_state: opp.location_state || '',
          location_country: opp.location_country || 'México',
          sponsor_name: opp.sponsor_name || '',
          sponsor_info: opp.sponsor_info || '',
          capital_target: opp.capital_target || '',
          capital_target_currency: opp.capital_target_currency || 'MXN',
          minimum_investment: opp.minimum_investment || '',
          maximum_investment: opp.maximum_investment || '',
          timeline_months: opp.timeline_months || '',
          hold_period_months: opp.hold_period_months || '',
          projected_irr_base: opp.projected_irr_base || '',
          projected_irr_upside: opp.projected_irr_upside || '',
          projected_irr_downside: opp.projected_irr_downside || '',
          projected_moic_base: opp.projected_moic_base || '',
          risk_level: opp.risk_level || 'medium',
          legal_structure: opp.legal_structure || '',
          fees_management: opp.fees_management || '',
          fees_performance: opp.fees_performance || '',
          fees_acquisition: opp.fees_acquisition || '',
          use_of_funds: opp.use_of_funds || '',
          is_featured: opp.is_featured || false,
          requires_accreditation: opp.requires_accreditation || false,
          gallery_images: opp.gallery_images || [],
          developer_name: opp.developer_name || '',
          operator_name: opp.operator_name || '',
          // Estructura de Capital
          capital_structure: opp.capital_structure || '',
          capital_equity: opp.capital_equity || '',
          capital_debt: opp.capital_debt || '',
          capital_mezzanine: opp.capital_mezzanine || '',
          // Uso de Fondos detallado
          use_of_funds_land: opp.use_of_funds_land || '',
          use_of_funds_construction: opp.use_of_funds_construction || '',
          use_of_funds_softcosts: opp.use_of_funds_softcosts || '',
          use_of_funds_reserves: opp.use_of_funds_reserves || '',
          use_of_funds_other: opp.use_of_funds_other || '',
          // Comisiones y Costos
          fees_structuring: opp.fees_structuring || '',
          fees_legal: opp.fees_legal || '',
          fees_other: opp.fees_other || '',
          // Supuestos Clave
          assumptions_occupancy: opp.assumptions_occupancy || '',
          assumptions_construction_cost: opp.assumptions_construction_cost || '',
          assumptions_sales_projection: opp.assumptions_sales_projection || '',
          assumptions_other: opp.assumptions_other || '',
        });
      } else {
        toast.error('Oportunidad no encontrada');
        navigate('/admin/opportunities');
      }
    } catch (error) {
      toast.error('Error al cargar oportunidad');
      navigate('/admin/opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };


  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (!isEditing) {
      toast.error('Guarde primero la oportunidad antes de subir imágenes');
      return;
    }
    
    setUploadingImage(true);
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('opportunity_id', opportunityId);
        
        const response = await api.post('/upload/project-image', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.image) {
          setFormData(prev => ({
            ...prev,
            gallery_images: [...prev.gallery_images, response.data.image]
          }));
          
          if (response.data.status === 'adjusted') {
            toast.info(response.data.message);
          } else {
            toast.success(response.data.message);
          }
        }
      } catch (error) {
        const msg = error.response?.data?.detail || `Error al subir ${file.name}`;
        toast.error(msg);
      }
    }
    
    setUploadingImage(false);
  };

  const removeImage = async (index) => {
    const img = formData.gallery_images[index];
    const imageId = img?.image_id;
    
    if (imageId && isEditing) {
      try {
        await api.delete(`/upload/project-image/${opportunityId}/${imageId}`);
        toast.success('Imagen eliminada');
      } catch (error) {
        toast.error('Error al eliminar la imagen del servidor');
      }
    }
    
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.slug || !formData.project_code) {
      toast.error('Complete los campos requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        capital_target: parseFloat(formData.capital_target) || 0,
        minimum_investment: parseFloat(formData.minimum_investment) || 0,
        maximum_investment: formData.maximum_investment ? parseFloat(formData.maximum_investment) : null,
        timeline_months: formData.timeline_months ? parseInt(formData.timeline_months) : null,
        hold_period_months: formData.hold_period_months ? parseInt(formData.hold_period_months) : null,
        projected_irr_base: formData.projected_irr_base ? parseFloat(formData.projected_irr_base) : null,
        projected_irr_upside: formData.projected_irr_upside ? parseFloat(formData.projected_irr_upside) : null,
        projected_irr_downside: formData.projected_irr_downside ? parseFloat(formData.projected_irr_downside) : null,
        projected_moic_base: formData.projected_moic_base ? parseFloat(formData.projected_moic_base) : null,
        fees_management: formData.fees_management ? parseFloat(formData.fees_management) : null,
        fees_performance: formData.fees_performance ? parseFloat(formData.fees_performance) : null,
        fees_acquisition: formData.fees_acquisition ? parseFloat(formData.fees_acquisition) : null,
      };
      
      if (isEditing) {
        await api.put(`/admin/opportunities/${opportunityId}`, payload);
        toast.success('Oportunidad actualizada');
      } else {
        await api.post('/admin/opportunities', payload);
        toast.success('Oportunidad creada');
      }
      
      navigate('/admin/opportunities');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-opportunity-form">
      <Link 
        to="/admin/opportunities" 
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
        Volver a oportunidades
      </Link>

      <div className="mb-8">
        <h1 className="heading-2 mb-2">
          {isEditing ? 'Editar Oportunidad' : 'Nueva Oportunidad'}
        </h1>
        <p className="text-muted-foreground">
          {isEditing ? 'Modifique los detalles de la oportunidad.' : 'Complete la información de la nueva oportunidad de inversión.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Información Básica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Título *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    handleInputChange('title', e.target.value);
                    if (!isEditing) {
                      handleInputChange('slug', generateSlug(e.target.value));
                    }
                  }}
                  placeholder="Nombre del proyecto"
                  data-testid="opp-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Título (Inglés)</Label>
                <Input
                  value={formData.title_en}
                  onChange={(e) => handleInputChange('title_en', e.target.value)}
                  placeholder="Project name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Slug (URL) *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  placeholder="proyecto-ejemplo"
                  data-testid="opp-slug"
                />
              </div>
              <div className="space-y-2">
                <Label>Código de Proyecto *</Label>
                <Input
                  value={formData.project_code}
                  onChange={(e) => handleInputChange('project_code', e.target.value)}
                  placeholder="SB-2025-001"
                  data-testid="opp-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => handleInputChange('status', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="under_review">En Revisión</SelectItem>
                    <SelectItem value="open">Abierta</SelectItem>
                    <SelectItem value="soft_circle">Soft Circle</SelectItem>
                    <SelectItem value="fully_funded">Fondeada</SelectItem>
                    <SelectItem value="closed">Cerrada</SelectItem>
                    <SelectItem value="archived">Archivada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Proyecto</Label>
                <Select
                  value={formData.project_type}
                  onValueChange={(v) => handleInputChange('project_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residencial</SelectItem>
                    <SelectItem value="commercial">Comercial</SelectItem>
                    <SelectItem value="office">Oficinas</SelectItem>
                    <SelectItem value="industrial">Industrial</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="mixed_use">Uso Mixto</SelectItem>
                    <SelectItem value="land">Terreno</SelectItem>
                    <SelectItem value="hospitality">Hotelería</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estructura de Inversión</Label>
                <Input
                  value={formData.investment_structure}
                  onChange={(e) => handleInputChange('investment_structure', e.target.value)}
                  placeholder="Ej: Equity, Deuda Mezzanine"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Resumen</Label>
              <Textarea
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                placeholder="Descripción breve del proyecto..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Tesis de Inversión</Label>
              <Textarea
                value={formData.thesis}
                onChange={(e) => handleInputChange('thesis', e.target.value)}
                placeholder="Por qué este proyecto representa una oportunidad atractiva..."
                rows={4}
              />
            </div>
          </CardContent>
        </Card>


        {/* Galería de Imágenes */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Image className="h-5 w-5" />
              Galería de Imágenes del Proyecto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
                id="gallery-upload"
                disabled={uploadingImage}
              />
              <label 
                htmlFor="gallery-upload" 
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                {uploadingImage ? (
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                ) : (
                  <Upload className="h-10 w-10 text-muted-foreground" />
                )}
                <div>
                  <span className="text-sm font-medium">
                    {uploadingImage ? 'Subiendo imágenes...' : 'Clic aquí para subir fotos del proyecto'}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG o WEBP • Máximo 20MB por imagen • Se procesan automáticamente
                  </p>
                </div>
              </label>
            </div>
            
            {formData.gallery_images && formData.gallery_images.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-3">{formData.gallery_images.length} imagen(es) subida(s)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.gallery_images.map((img, index) => (
                    <div key={index} className="relative group rounded-lg overflow-hidden border">
                      <img 
                        src={img.image_thumbnail_url || img.image_card_url || img.url} 
                        alt={img.caption || `Imagen ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={img.caption || ''}
                        onChange={(e) => {
                          const newImages = [...formData.gallery_images];
                          newImages[index] = { ...newImages[index], caption: e.target.value };
                          setFormData(prev => ({ ...prev, gallery_images: newImages }));
                        }}
                        placeholder="Descripción de la imagen..."
                        className="w-full text-xs p-2 border-t bg-background"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location */}

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Ubicación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                value={formData.location_address}
                onChange={(e) => handleInputChange('location_address', e.target.value)}
                placeholder="Calle, número, colonia"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Ciudad *</Label>
                <Input
                  value={formData.location_city}
                  onChange={(e) => handleInputChange('location_city', e.target.value)}
                  placeholder="Ciudad"
                />
              </div>
              <div className="space-y-2">
                <Label>Estado *</Label>
                <Input
                  value={formData.location_state}
                  onChange={(e) => handleInputChange('location_state', e.target.value)}
                  placeholder="Estado"
                />
              </div>
              <div className="space-y-2">
                <Label>País</Label>
                <Input
                  value={formData.location_country}
                  onChange={(e) => handleInputChange('location_country', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Información Financiera</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Capital Objetivo *</Label>
                <Input
                  type="number"
                  value={formData.capital_target}
                  onChange={(e) => handleInputChange('capital_target', e.target.value)}
                  placeholder="0"
                  data-testid="opp-capital"
                />
              </div>
              <div className="space-y-2">
                <Label>Inversión Mínima *</Label>
                <Input
                  type="number"
                  value={formData.minimum_investment}
                  onChange={(e) => handleInputChange('minimum_investment', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Inversión Máxima</Label>
                <Input
                  type="number"
                  value={formData.maximum_investment}
                  onChange={(e) => handleInputChange('maximum_investment', e.target.value)}
                  placeholder="Sin límite"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>TIR Base (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.projected_irr_base}
                  onChange={(e) => handleInputChange('projected_irr_base', e.target.value)}
                  placeholder="15"
                />
              </div>
              <div className="space-y-2">
                <Label>TIR Upside (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.projected_irr_upside}
                  onChange={(e) => handleInputChange('projected_irr_upside', e.target.value)}
                  placeholder="22"
                />
              </div>
              <div className="space-y-2">
                <Label>TIR Downside (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.projected_irr_downside}
                  onChange={(e) => handleInputChange('projected_irr_downside', e.target.value)}
                  placeholder="8"
                />
              </div>
              <div className="space-y-2">
                <Label>MOIC Base (x)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.projected_moic_base}
                  onChange={(e) => handleInputChange('projected_moic_base', e.target.value)}
                  placeholder="1.5"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Plazo del Proyecto (meses)</Label>
                <Input
                  type="number"
                  value={formData.timeline_months}
                  onChange={(e) => handleInputChange('timeline_months', e.target.value)}
                  placeholder="24"
                />
              </div>
              <div className="space-y-2">
                <Label>Periodo de Tenencia (meses)</Label>
                <Input
                  type="number"
                  value={formData.hold_period_months}
                  onChange={(e) => handleInputChange('hold_period_months', e.target.value)}
                  placeholder="36"
                />
              </div>
              <div className="space-y-2">
                <Label>Nivel de Riesgo</Label>
                <Select
                  value={formData.risk_level}
                  onValueChange={(v) => handleInputChange('risk_level', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Bajo</SelectItem>
                    <SelectItem value="medium">Medio</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Comisión Administración (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fees_management}
                  onChange={(e) => handleInputChange('fees_management', e.target.value)}
                  placeholder="2"
                />
              </div>
              <div className="space-y-2">
                <Label>Comisión Desempeño (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fees_performance}
                  onChange={(e) => handleInputChange('fees_performance', e.target.value)}
                  placeholder="20"
                />
              </div>
              <div className="space-y-2">
                <Label>Comisión Adquisición (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.fees_acquisition}
                  onChange={(e) => handleInputChange('fees_acquisition', e.target.value)}
                  placeholder="1"
                />
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Estructura de Capital */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Estructura de Capital</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Descripción de la Estructura</Label>
              <Textarea
                value={formData.capital_structure}
                onChange={(e) => handleInputChange('capital_structure', e.target.value)}
                placeholder="Describe la estructura de capital del proyecto..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Capital (Equity) %</Label>
                <Input
                  value={formData.capital_equity}
                  onChange={(e) => handleInputChange('capital_equity', e.target.value)}
                  placeholder="Ej: 40%"
                />
              </div>
              <div className="space-y-2">
                <Label>Deuda %</Label>
                <Input
                  value={formData.capital_debt}
                  onChange={(e) => handleInputChange('capital_debt', e.target.value)}
                  placeholder="Ej: 50%"
                />
              </div>
              <div className="space-y-2">
                <Label>Mezzanine %</Label>
                <Input
                  value={formData.capital_mezzanine}
                  onChange={(e) => handleInputChange('capital_mezzanine', e.target.value)}
                  placeholder="Ej: 10%"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Uso de Fondos Detallado */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Uso de Fondos (Detallado)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Adquisición de Terreno</Label>
                <Input
                  value={formData.use_of_funds_land}
                  onChange={(e) => handleInputChange('use_of_funds_land', e.target.value)}
                  placeholder="Ej: $15,000,000 MXN (30%)"
                />
              </div>
              <div className="space-y-2">
                <Label>Construcción</Label>
                <Input
                  value={formData.use_of_funds_construction}
                  onChange={(e) => handleInputChange('use_of_funds_construction', e.target.value)}
                  placeholder="Ej: $25,000,000 MXN (50%)"
                />
              </div>
              <div className="space-y-2">
                <Label>Soft Costs (Permisos, Diseño, Legal)</Label>
                <Input
                  value={formData.use_of_funds_softcosts}
                  onChange={(e) => handleInputChange('use_of_funds_softcosts', e.target.value)}
                  placeholder="Ej: $5,000,000 MXN (10%)"
                />
              </div>
              <div className="space-y-2">
                <Label>Reservas y Contingencias</Label>
                <Input
                  value={formData.use_of_funds_reserves}
                  onChange={(e) => handleInputChange('use_of_funds_reserves', e.target.value)}
                  placeholder="Ej: $3,000,000 MXN (6%)"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Otros Usos</Label>
              <Textarea
                value={formData.use_of_funds_other}
                onChange={(e) => handleInputChange('use_of_funds_other', e.target.value)}
                placeholder="Otros gastos o usos de fondos..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Comisiones y Costos */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Comisiones y Costos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Comisión de Estructuración</Label>
                <Input
                  value={formData.fees_structuring}
                  onChange={(e) => handleInputChange('fees_structuring', e.target.value)}
                  placeholder="Ej: 2% del capital"
                />
              </div>
              <div className="space-y-2">
                <Label>Costos Legales</Label>
                <Input
                  value={formData.fees_legal}
                  onChange={(e) => handleInputChange('fees_legal', e.target.value)}
                  placeholder="Ej: $500,000 MXN"
                />
              </div>
              <div className="space-y-2">
                <Label>Otros Costos</Label>
                <Input
                  value={formData.fees_other}
                  onChange={(e) => handleInputChange('fees_other', e.target.value)}
                  placeholder="Ej: Auditoría, seguros..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Supuestos Clave */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Supuestos Clave</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Ocupación Proyectada (según estudio de mercado)</Label>
              <Textarea
                value={formData.assumptions_occupancy}
                onChange={(e) => handleInputChange('assumptions_occupancy', e.target.value)}
                placeholder="Ej: Se estima una ocupación del 85% en el primer año..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Costos de Construcción (actualizados)</Label>
              <Textarea
                value={formData.assumptions_construction_cost}
                onChange={(e) => handleInputChange('assumptions_construction_cost', e.target.value)}
                placeholder="Ej: Costo estimado de $12,500 MXN/m²..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Proyecciones de Venta (conservadoras)</Label>
              <Textarea
                value={formData.assumptions_sales_projection}
                onChange={(e) => handleInputChange('assumptions_sales_projection', e.target.value)}
                placeholder="Ej: Precio de venta promedio de $45,000 MXN/m²..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Otros Supuestos</Label>
              <Textarea
                value={formData.assumptions_other}
                onChange={(e) => handleInputChange('assumptions_other', e.target.value)}
                placeholder="Inflación, tasas de interés, apreciación..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg">Información Adicional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Patrocinador</Label>
                <Input
                  value={formData.sponsor_name}
                  onChange={(e) => handleInputChange('sponsor_name', e.target.value)}
                  placeholder="Nombre del patrocinador/desarrollador"
                />
              </div>
              <div className="space-y-2">
                <Label>Estructura Legal</Label>
                <Input
                  value={formData.legal_structure}
                  onChange={(e) => handleInputChange('legal_structure', e.target.value)}
                  placeholder="Ej: SAPI, Fideicomiso"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre del Desarrollador</Label>
                <Input
                  value={formData.developer_name}
                  onChange={(e) => handleInputChange('developer_name', e.target.value)}
                  placeholder="Nombre que aparece sobre la foto del proyecto"
                  data-testid="developer-name-input"
                />
                <p className="text-xs text-muted-foreground">Se muestra en tarjetas y hero del proyecto</p>
              </div>
              <div className="space-y-2">
                <Label>Operador del Proyecto</Label>
                <Input
                  value={formData.operator_name}
                  onChange={(e) => handleInputChange('operator_name', e.target.value)}
                  placeholder="Nombre del operador (si es diferente al desarrollador)"
                  data-testid="operator-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Uso de Fondos</Label>
              <Textarea
                value={formData.use_of_funds}
                onChange={(e) => handleInputChange('use_of_funds', e.target.value)}
                placeholder="Descripción del uso de fondos..."
                rows={3}
              />
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => handleInputChange('is_featured', checked)}
                />
                <Label htmlFor="featured" className="cursor-pointer">Oportunidad Destacada</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="accredited"
                  checked={formData.requires_accreditation}
                  onCheckedChange={(checked) => handleInputChange('requires_accreditation', checked)}
                />
                <Label htmlFor="accredited" className="cursor-pointer">Requiere Inversionista Acreditado</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Link to="/admin/opportunities">
            <Button type="button" variant="outline">Cancelar</Button>
          </Link>
          <Button type="submit" disabled={saving} className="btn-primary gap-2" data-testid="save-opportunity">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" strokeWidth={1.5} />
            )}
            {isEditing ? 'Guardar Cambios' : 'Crear Oportunidad'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminOpportunityFormPage;
